import { Popover as PopoverPrimitive } from '@base-ui/react/popover';
import { Button } from '@/components/ui/button';
import { useTranslator } from '@/hooks/useTranslator';
import { flags } from '@/lib/flags';
import { useOnboardingTour } from './hooks/useOnboardingTour';

/**
 * First-run tour of the v2 shell — see `useOnboardingTour` for the step list.
 *
 * Uses the Base UI popover primitive rather than our `Popover` wrapper because
 * it anchors to an element the tour resolves at runtime, and the wrapper does
 * not forward `anchor`. The dimming layer is a plain fixed div and the
 * highlight is a CSS rule keyed on `data-tour-active`, so nothing here needs a
 * measured rect — no inline geometry, and the ring follows the control if it
 * moves or resizes.
 */
export function OnboardingTour() {
  const { getTranslation: t } = useTranslator({ page: 'shared' });
  const {
    open,
    step,
    anchor,
    virtualAnchor,
    stepNumber,
    totalSteps,
    isFirstStep,
    isLastStep,
    next,
    back,
    skip,
  } = useOnboardingTour();

  // `flags.onboarding` is how E2E turns tours off: the overlay below blocks
  // clicks, and a signed-in test session is indistinguishable from a first-time
  // user, so every spec would be fighting a tour.
  if (!flags.onboarding || !open || !step || !anchor || !virtualAnchor)
    return null;

  // The card content, rendered either anchored beside the target or centred in
  // the viewport — see `centered` on the step.
  const card = (
    <>
      <div className='flex items-baseline justify-between gap-3'>
        <p className='text-sm font-semibold'>
          {t(`onboardingTour.steps.${step.key}.title`)}
        </p>
        <span className='shrink-0 text-xs text-muted-foreground'>
          {t('onboardingTour.progress', {
            current: stepNumber,
            total: totalSteps,
          })}
        </span>
      </div>

      <p className='text-xs leading-relaxed text-muted-foreground'>
        {t(`onboardingTour.steps.${step.key}.body`)}
      </p>

      <div className='flex items-center justify-between gap-2'>
        <Button variant='ghost' onClick={skip}>
          {t('onboardingTour.skip')}
        </Button>
        <div className='flex items-center gap-2'>
          {!isFirstStep && (
            <Button variant='outline' onClick={back}>
              {t('onboardingTour.back')}
            </Button>
          )}
          <Button onClick={next}>
            {isLastStep ? t('onboardingTour.finish') : t('onboardingTour.next')}
          </Button>
        </div>
      </div>
    </>
  );

  const cardClassName =
    'flex w-80 flex-col gap-3 rounded-lg bg-popover p-4 text-popover-foreground shadow-popover ring-1 ring-foreground/10 outline-hidden animate-in fade-in-0 zoom-in-95 duration-150';

  return (
    <>
      {/* Dims the app, and nothing more: clicking beside the card is as often a
          stray click as a decision, so it must not end the tour. Skip and Done
          are the only ways out. Inert and aria-hidden — it is decoration, not a
          control, and it blocks clicks on the app underneath while open. */}
      <div
        aria-hidden
        className='fixed inset-0 z-300 bg-black/50 animate-in fade-in duration-200'
      />

      {step.centered ? (
        <div className='fixed inset-0 z-310 flex items-center justify-center p-4'>
          <div className={cardClassName}>{card}</div>
        </div>
      ) : (
        <PopoverPrimitive.Root open modal={false}>
          <PopoverPrimitive.Portal>
            <PopoverPrimitive.Positioner
              anchor={virtualAnchor}
              side={step.side}
              align='center'
              sideOffset={12}
              // Without padding the card can end up flush against — or past —
              // the viewport edge, which clipped its title.
              collisionPadding={16}
              className='isolate z-310'
            >
              <PopoverPrimitive.Popup className={cardClassName}>
                {card}
              </PopoverPrimitive.Popup>
            </PopoverPrimitive.Positioner>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
      )}
    </>
  );
}
